import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useReducedMotion, reduceMotion, DURATION_SLOW, EASE_OUT } from '@/lib/motion';
import { Button } from '@/components/ui';

type ScreenState = 'ok' | 'warning' | 'blocked';

const BLOCKED_QUERY = '(min-width: 1024px)';
const WARNING_QUERY = '(min-width: 1280px)';
const WARNING_TOAST_ID = 'screen-size-warning';

function getScreenState(blocked: boolean, warning: boolean): ScreenState {
  if (!blocked) return 'blocked';
  if (!warning) return 'warning';
  return 'ok';
}

export function ScreenSizeOverlay() {
  const [screenState, setScreenState] = useState<ScreenState>('ok');
  const [dismissed, setDismissed] = useState(false);
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    const blockedMql = window.matchMedia(BLOCKED_QUERY);
    const warningMql = window.matchMedia(WARNING_QUERY);

    const update = () => {
      const next = getScreenState(blockedMql.matches, warningMql.matches);
      setScreenState((prev) => {
        if (next !== prev) setDismissed(false);
        return next;
      });
    };

    update();
    blockedMql.addEventListener('change', update);
    warningMql.addEventListener('change', update);
    return () => {
      blockedMql.removeEventListener('change', update);
      warningMql.removeEventListener('change', update);
    };
  }, []);

  useEffect(() => {
    if (screenState === 'warning') {
      toast.warning(
        'For the best experience, use a screen width of 1280px or wider.',
        {
          id: WARNING_TOAST_ID,
          duration: Infinity,
          position: 'top-right',
        },
      );
    } else {
      toast.dismiss(WARNING_TOAST_ID);
    }
  }, [screenState]);

  const handleDismiss = useCallback(() => setDismissed(true), []);

  const fade = reduceMotion(prefersReduced, {
    duration: DURATION_SLOW,
    ease: EASE_OUT,
  });

  const showBlocked = screenState === 'blocked' && !dismissed;

  return (
    <AnimatePresence>
      {showBlocked && (
        <motion.div
          key="screen-size-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={fade}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-6"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={fade}
            className="flex flex-col items-center gap-6 text-center max-w-xs bg-bg-primary/80 backdrop-blur-sm rounded-2xl p-8 border border-border-primary"
          >
            <motion.div
              animate={prefersReduced ? {} : { scale: [1, 1.08, 1] }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="relative flex items-center justify-center"
            >
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-accent-primary/10">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-accent-primary"
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>

              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute -left-4 text-text-secondary"
                animate={prefersReduced ? {} : { x: [0, -4, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <polyline points="15 18 9 12 15 6" />
              </motion.svg>

              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute -right-4 text-text-secondary"
                animate={prefersReduced ? {} : { x: [0, 4, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              >
                <polyline points="9 18 15 12 9 6" />
              </motion.svg>
            </motion.div>

            <div className="space-y-2">
              <h2 className="heading-sm-medium text-text-primary">
                Screen size not supported
              </h2>
              <p className="body-sm-regular text-text-secondary">
                NVX requires a wider screen to work properly. Please resize your
                browser window or switch to a device with a larger display.
              </p>
            </div>

            <Button variant="ghost" size="sm" onClick={handleDismiss}>
              Continue anyway
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
